import { NUMBER } from 'components/keyFigures/KeyFigureConstants'
import { push } from '@lagunovsky/redux-react-router'
import { groupBy, maxBy, orderBy, keys, compact } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ReferencialAction from '../../referencial/action/ReferencialAction'
import { getDate, getHour, getYearOrString } from '../../utils/DateUtil'
import { getPercentage, hasValue, round } from '../../utils/NumberUtil'
import { getLabel } from '../../utils/StoreUtils'
import { i18nize, normalizeString } from '../../utils/StringUtil'
import { CAMPAIGN_STATUT_FOR_FILTER, CAMPAIGN_STATUT_VALUES, CAMPAIGN_TYPES, DEFAULT_OBJECTIVE_SUFFIX, OBJECTIVE_SUFFIX_BY_CAMPAIGN_TYPE } from '../constants/CampaignConstants'
import CampaignDto from '../dto/CampaignDto'
import DtoCampaignProgress from '../dto/DtoCampaignProgress'
import CampaignCard from './CampaignCard'
import { getCampaignContributorIds } from '../utils/CampaignUtils'
import FormFilterCampaigns from './FormFilterCampaigns'
import SimpleKeyFigurePanel from '../../components/keyFigures/SimpleKeyFigurePanel'
import { CardContent, Stack, Card, Grid, Accordion, AccordionDetails, AccordionSummary, Typography, LinearProgress } from '@mui/material'
import { CardTitle } from '../../components/card/NewCard'
import ColorConstant from '../../components/constants/ColorConstant'
import SimpleTabList from 'components/list/SimpleTabList'
import useActions from 'utils/customHook/useActions'
import { NoDataPanel } from 'components/card/NoDataCard'
import { authorizeContributors } from 'utils/HabilitationUtil'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useSessionStorage from 'utils/customHook/useSessionStorage'
import useCampaignBookmarks from '../../utils/customHook/useCampaignBookmarks'
import UserAction from '../../administration/components/user/actions/UserAction'

const STATUT = 'statut'
const DATE = 'date'
const REFERENT = 'referent'
const TYPE = 'type'
const LABO = 'labo'

const EMPTY_FILTER = {
    searchValue: '',
    startDate: '',
    endDate: '',
    type: null,
    statut: null,
    labo: null,
    objectives: [],
    bookmarksOnly: false,
}

const DashboardCampaigns = ({
    campaigns,
    campaignsProgress,
    stationType,
}) => {
    // filtres et onglet conservés le temps de la session : on les retrouve en revenant d'une campagne
    const [filter, setFilter] = useSessionStorage(`CAMPAIGNS_FILTER_${stationType}`, EMPTY_FILTER)
    const [selectedTab, setSelectedTab] = useSessionStorage(`CAMPAIGNS_TAB_${stationType}`, STATUT)

    const {
        contacts,
        sandreCodes,
        accountUser,
        contributors,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        accountUser: store.AccountReducer.accountUser,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { isBookmarkable, bookmarkIds } = useCampaignBookmarks(stationType)

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        dispatch(UserAction.fetchBookmarks())
    }, [])

    const getCampaignGroupByType = (campaignList = campaigns) => groupBy(campaignList, c => {
        switch (c.statut) {
            case 1: return 'dataDrafts'
            case 2: return 'dataCurrent'
            case 3: return 'dataCompleted'
            case 4: return 'dataArchived'
            default: return 'dataWithoutStatut'
        }
    })

    const getHash = campaign => normalizeString(['name', 'recipientCode'].map(key => campaign[key]).join() + getDate(campaign.startDate) + getDate(campaign.endDate))

    const filteredCampaigns = useMemo(() => {
        const {
            searchValue,
            startDate,
            endDate,
            type,
            statut,
            labo,
            objectives,
            bookmarksOnly,
        } = filter
        const {
            dataDrafts = [],
            dataCurrent = [],
            dataCompleted = [],
            dataArchived = [],
        } = getCampaignGroupByType()
        const campaignsByStatus = (() => {
            switch (statut) {
                case CAMPAIGN_STATUT_VALUES.IN_ANTICIPATION: return dataDrafts
                case CAMPAIGN_STATUT_VALUES.IN_PROGRESS: return dataCurrent
                case CAMPAIGN_STATUT_VALUES.FINALIZE: return dataCompleted
                case CAMPAIGN_STATUT_VALUES.ARCHIVED: return dataArchived
                default: return campaigns
            }
        })()
        const searchCharacters = normalizeString(searchValue)
        const filterStatus = hasValue(searchValue) ? campaignsByStatus.filter(c => getHash(c).includes(searchCharacters)) : campaignsByStatus
        const filterStartDate = hasValue(startDate) ? filterStatus.filter(c => +moment(c.beginningApplication, 'DD/MM/YYYY') >= startDate) : filterStatus
        const filterEndDate = hasValue(endDate) ? filterStartDate.filter(c => +moment(c.endingApplication, 'DD/MM/YYYY') <= endDate) : filterStartDate
        const filterLabo = hasValue(labo) ? filterEndDate.filter(c => getCampaignContributorIds(c).includes(parseInt(labo))) : filterEndDate
        const filterObjectives = objectives?.length ? filterLabo.filter(c => objectives.some(o => (c.objectives || []).includes(o))) : filterLabo
        const filterType = !hasValue(type) || type == -1 ? filterObjectives : filterObjectives.filter(c => c.campaignType == type)
        return bookmarksOnly ? filterType.filter(c => bookmarkIds.includes(c.id)) : filterType
    }, [campaigns, filter, bookmarkIds])

    const bookmarkedCampaigns = useMemo(() => filteredCampaigns.filter(c => bookmarkIds.includes(c.id)), [filteredCampaigns, bookmarkIds])

    const getObjectivesLabel = campaign => {
        const suffix = hasValue(campaign.campaignType) ? OBJECTIVE_SUFFIX_BY_CAMPAIGN_TYPE[campaign.campaignType] ?? DEFAULT_OBJECTIVE_SUFFIX : undefined
        if (!suffix) {
            return ''
        }
        const field = `CAUSE_INTERVENTION.${suffix}`
        return (campaign.objectives || [])
            .map(objective => sandreCodes.find(sc => sc.field === field && sc.code === objective)?.name)
            .filter(name => !!name)
            .join(', ')
    }

    const getCampaignExport = () => {
        const data = orderBy(filteredCampaigns, c => moment(c.beginningApplication, 'DD/MM/YYYY').valueOf(), 'desc').map(c => {
            const {
                nbStation = 0,
                nbStationValidated = 0,
                nbDoneEvents = 0,
                nbNotDoneEvents = 0,
            } = campaignsProgress.find(cp => cp.id === c.id) || {}
            return {
                name: c.name,
                startDate: { value: c.beginningApplication, format: 'dd/MM/yyyy', cellType: 'date' },
                endDate: { value: c.endingApplication, format: 'dd/MM/yyyy', cellType: 'date' },
                objectives: getObjectivesLabel(c),
                stationsNumber: { value: nbStation, format: '0', cellType: 'number' },
                contolStationNumber: { value: nbStationValidated, format: '0', cellType: 'number' },
                progressionPercent: { value: getPercentage(nbStationValidated, nbStation), format: '0', cellType: 'number' },
                nbDoneInterventions: { value: nbDoneEvents, format: '0', cellType: 'number' },
                nbNotDoneInterventions: { value: nbNotDoneEvents, format: '0', cellType: 'number' },
                lastUpdate: { value: moment(c.updateDate).format('DD/MM/YYYY'), format: 'dd/MM/yyyy', cellType: 'date' },
                lastUpdateLogin: c.updateLogin,
                statuts: getLabel(i18nize(CAMPAIGN_STATUT_FOR_FILTER), c.statut),
                campaignType: getLabel(i18nize(CAMPAIGN_TYPES), c.campaignType) || i18n.unknownType,
            }
        })
        if (data.length) {
            data[0].headers = keys(data[0])
        }
        return data
    }

    useActions(() => {
        const exportAction = {
            export: () => ({
                data: getCampaignExport(),
                exportType: 'xlsx',
                titleFile: `${i18n.campaigns}_${i18n[stationType]}`,
            }),
        }
        return accountUser.consultant === '1' ?
            exportAction
            :
            {
                ...exportAction,
                new: () => dispatch(push(`/${stationType}/campaign/new/dashboard`)),
            }
    }, [stationType, filteredCampaigns, campaignsProgress, sandreCodes, accountUser.consultant])

    const getCampaignCard = campaign => {
        const {
            id,
            contactCode,
        } = campaign
        const contact = contacts.find(c => contactCode === c.code)
        const progress = campaignsProgress.find(cp => cp.id === id)
        return (
            <CampaignCard
                key={id}
                campaign={campaign}
                progress={progress}
                contact={contact}
                stationType={stationType}
                isBookmarked={isBookmarkable ? bookmarkIds.includes(id) : undefined}
            />
        )
    }

    const getPanelList = (groups, title) => keys(groups).map(key => {
        const groupCard = orderBy(groups[key], 'statut').map(c => getCampaignCard(c))
        return (
            <Accordion key={key} defaultExpanded>
                <AccordionSummary>
                    {title || key}
                </AccordionSummary>
                <AccordionDetails>
                    {groupCard}
                </AccordionDetails>
            </Accordion>
        )
    })

    const getPanelListStatut = (groups, title) => {
        const groupCard = groups.map(c => getCampaignCard(c))
        return (
            <Accordion defaultExpanded>
                <AccordionSummary>
                    {title}
                </AccordionSummary>
                <AccordionDetails>
                    {groupCard}
                </AccordionDetails>
            </Accordion>
        )
    }

    const getCampaignByReferent = () => {
        const groups = groupBy(
            filteredCampaigns,
            c => hasValue(c.contactCode) ? getLabel(contacts, c.contactCode) : i18n.unknownReferent,
        )
        return (
            <Stack spacing={1}>
                {getPanelList(groups)}
            </Stack>
        )
    }

    const getCampaignByLaboratory = () => {
        const groups = groupBy(
            orderBy(filteredCampaigns, 'recipientCode'),
            c => hasValue(c.recipientCode) ? getLabel(authorizeContributors(contributors, accountUser, parseInt(c.recipientCode)), c.recipientCode) : i18n.unknownLaboratory,
        )
        return (
            <Stack spacing={1}>
                {getPanelList(groups)}
            </Stack>
        )
    }

    const getCampaignByType = () => {
        const groups = groupBy(
            filteredCampaigns,
            ({ campaignType = 4 }) => campaignType,
        )
        const campaignTypes = i18nize(CAMPAIGN_TYPES)
        const list = keys(groups).map(key => {
            const groupCard = groups[key].map(c => getCampaignCard(c))
            const title = getLabel(campaignTypes, key) || i18n.unknownType
            return (
                <Accordion key={key} defaultExpanded>
                    <AccordionSummary>
                        {title || key}
                    </AccordionSummary>
                    <AccordionDetails>
                        {groupCard}
                    </AccordionDetails>
                </Accordion>
            )
        })
        return (
            <Stack spacing={1}>
                {list}
            </Stack>
        )
    }

    const getKeyFigures = ({ nbCampaignNotDraft, nbCampaignValidated, nbStation }) => [{
        title: 'inProgress',
        value: nbCampaignNotDraft - nbCampaignValidated,
        typeValue: NUMBER,
    }, {
        title: 'total',
        value: campaigns.length,
        typeValue: NUMBER,
    }, {
        title: 'stations',
        value: nbStation,
        typeValue: NUMBER,
    }]

    const getLastUpdates = () => {
        const filterCampaignUpdate = filteredCampaigns.filter(o => o.updateDate && o.name)
        const lastCampaignUpdate = maxBy(filterCampaignUpdate, 'updateDate')
        if (lastCampaignUpdate) {
            const updateLogin = lastCampaignUpdate.updateLogin ? lastCampaignUpdate.updateLogin : i18n.unknown
            const updateDate = getDate(lastCampaignUpdate.updateDate, 'DD/MM/YYYY')
            const updateHour = getHour(lastCampaignUpdate.updateDate, 'HH:mm')
            return (
                <Stack spacing={1}>
                    <Typography fontWeight={'bold'}>{lastCampaignUpdate.name}</Typography>
                    {`${i18n.by} ${updateLogin} ${i18n.atDate} ${updateDate} ${i18n.atHour} ${updateHour}`}
                </Stack>
            )
        }
        return (
            <NoDataPanel />
        )
    }

    const groupByYear = data => groupBy(data, c => getYearOrString(c.beginningApplication, i18n.thisYear))

    const getCampaignByStatut = () => {
        const {
            dataDrafts = [],
            dataCurrent = [],
            dataCompleted = [],
            dataArchived = [],
            dataWithoutStatut = [],
        } = getCampaignGroupByType(filteredCampaigns)
        return (
            <Stack spacing={1}>
                {!!dataDrafts.length && getPanelListStatut(dataDrafts, i18n.inAnticipation)}
                {!!dataCurrent.length && getPanelListStatut(dataCurrent, i18n.inProgress)}
                {!!dataCompleted.length && getPanelListStatut(dataCompleted, i18n.finalize)}
                {!!dataArchived.length && getPanelListStatut(dataArchived.map(a => ({ ...a, isArchived: true })), i18n.archived)}
                {!!dataWithoutStatut.length && getPanelListStatut(dataWithoutStatut, i18n.unknowStatus)}
            </Stack>
        )
    }

    const getCampaignByDate = () => {
        return (
            <Stack spacing={1}>
                {getPanelList(groupByYear(filteredCampaigns))}
            </Stack>
        )
    }

    const getProgressCard = (title, total, nb) => {
        const percentage = total ? Math.min((nb / total) * 100, 100) : 0
        const barColor = (percentage === 100 ? ColorConstant.GREEN : ColorConstant.ORANGE).toLowerCase()
        return (
            <Card>
                <CardTitle title={title} />
                <CardContent>
                    <Stack spacing={1}>
                        <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                            <Typography variant='body2' color='text.secondary'>{`${round(nb)} / ${round(total)}`}</Typography>
                            <Typography fontWeight='bold'>{`${round(percentage)} %`}</Typography>
                        </Stack>
                        <LinearProgress
                            variant='determinate'
                            value={percentage}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 5 },
                            }}
                        />
                    </Stack>
                </CardContent>
            </Card>
        )
    }

    const getEmpty = () => {
        return (
            <div style={{ background: 'white', border: '1px solid #35609F' }}>
                <div style={{ padding: '10%' }}>
                    <Grid size={12} sx={{ textAlign: 'center' }}>
                        <i className='material-icons medium'>nature_people</i>
                    </Grid>
                    <div className='center font-size-20'>{i18n.NoneResultForThisResearch}</div>
                </div>
            </div>
        )
    }

    const keyFigures = campaignsProgress.reduce((acc, cp) => {
        const camp = campaigns.find(c => c.id === cp.id)
        return {
            nbStation: acc.nbStation + cp.nbStation,
            nbStationValidated: acc.nbStationValidated + cp.nbStationValidated,
            nbCampaignValidated: camp && camp.statut !== 1 && cp.nbStation !== 0 && cp.nbStation === cp.nbStationValidated ? acc.nbCampaignValidated + 1 : acc.nbCampaignValidated,
            progressTotal: acc.progressTotal + cp.progressTotal,
            progress: acc.progress + cp.progress,
        }
    }, { nbStation: 0, nbStationValidated: 0, nbCampaignValidated: 0, progressTotal: 0, progress: 0 })

    const nbCampaignNotDraft = campaigns.filter(c => c.statut !== 1).length

    return (
        <Grid container spacing={1}>
            <Grid size={9}>
                <FormFilterCampaigns
                    filter={filter}
                    onChange={setFilter}
                    onValidate={setFilter}
                    stationType={stationType}
                    campaigns={campaigns}
                    onReset={() => setFilter(EMPTY_FILTER)}
                    isBookmarkable={isBookmarkable}
                />
                {!filteredCampaigns.length && (filter.startDate ||
                        filter.endDate || filter.statut ||
                        filter.type || filter.searchValue || filter.labo ||
                        filter.objectives?.length || filter.bookmarksOnly) ?
                    getEmpty()
                    :
                    (<Grid size={12} paddingTop={1}>
                        <SimpleTabList
                            defaultTab={STATUT}
                            controlTab={selectedTab}
                            setControlTab={setSelectedTab}
                            tabs={compact([
                                {
                                    constant: STATUT,
                                    label: i18n.byStatus,
                                    icon: 'edit',
                                },
                                {
                                    constant: DATE,
                                    label: i18n.byDate,
                                    icon: 'event',
                                },
                                {
                                    constant: REFERENT,
                                    label: i18n.byReferent,
                                    icon: 'person',
                                },
                                {
                                    constant: TYPE,
                                    label: i18n.byCampaignType,
                                    icon: 'business_center',
                                },
                                stationType === STATION_TYPE_NAME.quality ? {
                                    constant: LABO,
                                    label: i18n.byLaboratory,
                                    icon: 'biotech',
                                } : undefined,
                            ])}
                        >
                            {(tab) => (
                                <Stack spacing={1}>
                                    {!!bookmarkedCampaigns.length && getPanelListStatut(bookmarkedCampaigns, i18n.bookmarks)}
                                    {tab === REFERENT && getCampaignByReferent()}
                                    {tab === TYPE && getCampaignByType()}
                                    {tab === DATE && getCampaignByDate()}
                                    {tab === STATUT && getCampaignByStatut()}
                                    {tab === LABO && getCampaignByLaboratory()}
                                </Stack>
                            )}
                        </SimpleTabList>
                    </Grid>)
                }
            </Grid>
            <Grid size={3}>
                <Stack spacing={1}>
                    <SimpleKeyFigurePanel
                        data={getKeyFigures({ ...keyFigures, nbCampaignNotDraft })}
                        showAllValues
                    />
                    {getProgressCard(i18n.campaignsProgress, nbCampaignNotDraft, keyFigures.nbCampaignValidated)}
                    {getProgressCard(i18n.visitsProgress, keyFigures.progressTotal, keyFigures.progress)}
                    <Card>
                        <CardTitle title={i18n.lastUpdate} />
                        <CardContent>
                            {getLastUpdates()}
                        </CardContent>
                    </Card>
                </Stack>
            </Grid>
        </Grid>
    )
}

DashboardCampaigns.propTypes = {
    campaigns: PropTypes.arrayOf(PropTypes.instanceOf(CampaignDto)),
    campaignsProgress: PropTypes.arrayOf(PropTypes.instanceOf(DtoCampaignProgress)),
    stationType: PropTypes.string,
}

export default DashboardCampaigns
